/*
 * The four chart shapes from html-markup/assets/js/charts.js.
 *
 *   BarChart    vertical bars              Recharts <BarChart>
 *   LineChart   line + area, 1..n series   Recharts <AreaChart>
 *   DonutChart  ring with a centre label   Recharts <PieChart>
 *   HBars       ranked horizontal list     plain HTML — see the file for why
 *
 * Call signatures match the markup's, so porting a page's chart call is a copy
 * rather than a rewrite. Colours always come from the caller as Tailwind tones:
 * one hue per meaning, decided by the page, never picked here.
 *
 * THE THREE RECHARTS SHAPES ARE LAZY. Measured: Recharts costs +360 kB raw,
 * +105 kB gzipped — it nearly doubles the bundle. Loading it eagerly would make
 * every page pay for it, including login, users and settings, which draw no
 * charts at all. Behind a dynamic import it becomes one cacheable chunk that
 * arrives only on pages that actually plot something, and it can never delay
 * first paint.
 *
 * All three resolve from ONE import() so they share a single chunk rather than
 * pulling three copies of the same library, and the wrappers keep the call
 * signature identical — a page does not know or care that it is lazy.
 */
import { lazy, Suspense } from "react";

export { default as HBars } from "./HBars.jsx";
export { ChartEmpty, ChartTooltip } from "./ChartShell.jsx";

const load = () => import("./recharts.js");

const LazyBar = lazy(() => load().then((m) => ({ default: m.BarChart })));
const LazyLine = lazy(() => load().then((m) => ({ default: m.LineChart })));
const LazyDonut = lazy(() => load().then((m) => ({ default: m.DonutChart })));

/**
 * Holds the chart's height while the chunk arrives, so the page does not jump
 * when it lands. Deliberately quiet — a spinner for something that is usually
 * a few hundred milliseconds reads as an error.
 */
function ChartSkeleton({ height = 220 }) {
    return (
        <div
            data-chart-loading
            className="w-full animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800/60"
            style={{ height }}
            aria-hidden="true"
        />
    );
}

export function BarChart(props) {
    return (
        <Suspense fallback={<ChartSkeleton height={props.height ?? 220} />}>
            <LazyBar {...props} />
        </Suspense>
    );
}

export function LineChart(props) {
    return (
        <Suspense fallback={<ChartSkeleton height={props.height ?? 240} />}>
            <LazyLine {...props} />
        </Suspense>
    );
}

export function DonutChart(props) {
    return (
        <Suspense fallback={<ChartSkeleton height={props.size ?? 176} />}>
            <LazyDonut {...props} />
        </Suspense>
    );
}
